<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-breadcrumbs :items="breadcrumbs">
          <template v-slot:divider>
            <v-icon>mdi-forward</v-icon>
          </template>
        </v-breadcrumbs>
      </v-col>

      <v-col cols="12">
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-library-shelves</v-icon>
            Learning Resources Management
          </v-card-title>

          <v-card-text class="pa-0">
            <v-tabs
              v-model="activeTab"
              background-color="grey lighten-4"
              color="primary"
              show-arrows
            >
              <v-tab href="#tab-ebooks">
                <v-icon left small>mdi-book-open-page-variant</v-icon>
                eBooks
              </v-tab>
              <v-tab href="#tab-links">
                <v-icon left small>mdi-link-variant</v-icon>
                External Links
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <!-- ===== eBOOKS TAB ===== -->
              <v-tab-item value="tab-ebooks">
                <v-container class="pa-4">
                  <v-row>
                    <v-col cols="12">
                      <h3 class="section-subtitle mb-4">Add New eBook</h3>
                    </v-col>
                  </v-row>

                  <v-form @submit.prevent="submitEbook">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          value="eBook"
                          label="Resource Type"
                          outlined
                          dense
                          readonly
                          disabled
                          prepend-inner-icon="mdi-book-open-page-variant"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model.trim="ebookForm.title"
                          label="Title *"
                          outlined
                          dense
                          counter="150"
                          :error-messages="ebookTitleErrors"
                          @input="$v.ebookForm.title.$touch()"
                          @blur="$v.ebookForm.title.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12">
                        <v-textarea
                          v-model.trim="ebookForm.description"
                          label="Description"
                          outlined
                          dense
                          rows="2"
                          counter="500"
                          :error-messages="ebookDescriptionErrors"
                          @input="$v.ebookForm.description.$touch()"
                          @blur="$v.ebookForm.description.$touch()"
                        ></v-textarea>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model.trim="ebookForm.fileUrl"
                          label="File URL *"
                          outlined
                          dense
                          counter="500"
                          prepend-inner-icon="mdi-file-pdf-box"
                          :error-messages="ebookFileUrlErrors"
                          @input="$v.ebookForm.fileUrl.$touch()"
                          @blur="$v.ebookForm.fileUrl.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.trim="ebookForm.author"
                          label="Author"
                          outlined
                          dense
                          counter="100"
                          :error-messages="ebookAuthorErrors"
                          @input="$v.ebookForm.author.$touch()"
                          @blur="$v.ebookForm.author.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.trim="ebookForm.fileSize"
                          label="File Size (bytes)"
                          outlined
                          dense
                          type="number"
                          :error-messages="ebookFileSizeErrors"
                          @input="$v.ebookForm.fileSize.$touch()"
                          @blur="$v.ebookForm.fileSize.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model.trim="ebookForm.coverImageUrl"
                          label="Cover Image URL"
                          outlined
                          dense
                          counter="500"
                          prepend-inner-icon="mdi-image-outline"
                          :error-messages="ebookCoverImageUrlErrors"
                          @input="$v.ebookForm.coverImageUrl.$touch()"
                          @blur="$v.ebookForm.coverImageUrl.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" class="text-right">
                        <v-btn
                          text
                          class="mr-2"
                          @click="resetEbookForm"
                          :disabled="ebookSubmitting"
                          >Clear</v-btn
                        >
                        <v-btn
                          color="primary"
                          type="submit"
                          :loading="ebookSubmitting"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Add eBook
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>

                  <v-divider class="my-6"></v-divider>

                  <v-data-table
                    :headers="ebookHeaders"
                    :items="getEbooks"
                    :search="ebookSearch"
                    :loading="!isEbookListLoaded"
                    loading-text="Loading eBooks..."
                    item-key="resource_id"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-toolbar flat>
                        <v-toolbar-title>eBooks on System</v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-text-field
                          v-model="ebookSearch"
                          append-icon="mdi-magnify"
                          label="Search"
                          single-line
                          hide-details
                          dense
                          clearable
                        ></v-text-field>
                      </v-toolbar>
                    </template>

                    <template v-slot:[`item.title`]="{ item }">
                      <span
                        :class="item.status === 1 ? '' : 'text--disabled'"
                        >{{ item.title }}</span
                      >
                    </template>

                    <template v-slot:[`item.description`]="{ item }">
                      {{ item.description || "-" }}
                    </template>

                    <template v-slot:[`item.author`]="{ item }">
                      {{ item.author || "-" }}
                    </template>

                    <template v-slot:[`item.file_size`]="{ item }">
                      {{ formatFileSize(item.file_size) }}
                    </template>

                    <template v-slot:[`item.file_url`]="{ item }">
                      <a
                        v-if="item.file_url"
                        :href="item.file_url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <v-icon small color="primary">mdi-open-in-new</v-icon>
                        Open
                      </a>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:[`item.created_on`]="{ item }">
                      {{ item.created_on | dateReOrderRMtime }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn
                        v-if="item.status === 1"
                        icon
                        color="error"
                        title="Remove resource"
                        @click.stop="askDeleteEbook(item)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                      <v-chip v-else small color="grey" text-color="white">
                        Record removed
                      </v-chip>
                    </template>

                    <template v-slot:no-data>
                      <v-alert type="info" outlined class="ma-4">
                        No eBooks found. Add one above.
                      </v-alert>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>

              <!-- ===== EXTERNAL LINKS TAB ===== -->
              <v-tab-item value="tab-links">
                <v-container class="pa-4">
                  <v-row>
                    <v-col cols="12">
                      <h3 class="section-subtitle mb-4">
                        Add New External Link
                      </h3>
                    </v-col>
                  </v-row>

                  <v-form @submit.prevent="submitLink">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="linkForm.type"
                          :items="linkTypes"
                          label="Resource Type *"
                          outlined
                          dense
                          :error-messages="linkTypeErrors"
                          @change="$v.linkForm.type.$touch()"
                          @blur="$v.linkForm.type.$touch()"
                        ></v-select>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model.trim="linkForm.title"
                          label="Title *"
                          outlined
                          dense
                          counter="150"
                          :error-messages="linkTitleErrors"
                          @input="$v.linkForm.title.$touch()"
                          @blur="$v.linkForm.title.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12">
                        <v-textarea
                          v-model.trim="linkForm.description"
                          label="Description"
                          outlined
                          dense
                          rows="2"
                          counter="500"
                          :error-messages="linkDescriptionErrors"
                          @input="$v.linkForm.description.$touch()"
                          @blur="$v.linkForm.description.$touch()"
                        ></v-textarea>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model.trim="linkForm.url"
                          label="URL *"
                          outlined
                          dense
                          counter="500"
                          prepend-inner-icon="mdi-link-variant"
                          :error-messages="linkUrlErrors"
                          @input="$v.linkForm.url.$touch()"
                          @blur="$v.linkForm.url.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model.trim="linkForm.platform"
                          label="Platform"
                          outlined
                          dense
                          counter="100"
                          hint="e.g. YouTube, GOV.UK"
                          :error-messages="linkPlatformErrors"
                          @input="$v.linkForm.platform.$touch()"
                          @blur="$v.linkForm.platform.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" md="8">
                        <v-text-field
                          v-model.trim="linkForm.coverImageUrl"
                          label="Cover Image URL"
                          outlined
                          dense
                          counter="500"
                          prepend-inner-icon="mdi-image-outline"
                          :error-messages="linkCoverImageUrlErrors"
                          @input="$v.linkForm.coverImageUrl.$touch()"
                          @blur="$v.linkForm.coverImageUrl.$touch()"
                        ></v-text-field>
                      </v-col>

                      <v-col cols="12" class="text-right">
                        <v-btn
                          text
                          class="mr-2"
                          @click="resetLinkForm"
                          :disabled="linkSubmitting"
                          >Clear</v-btn
                        >
                        <v-btn
                          color="primary"
                          type="submit"
                          :loading="linkSubmitting"
                        >
                          <v-icon left>mdi-plus</v-icon>
                          Add External Link
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-form>

                  <v-divider class="my-6"></v-divider>

                  <v-data-table
                    :headers="linkHeaders"
                    :items="getExternalLinks"
                    :search="linkSearch"
                    :loading="!isExternalLinkListLoaded"
                    loading-text="Loading external links..."
                    item-key="resource_id"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-toolbar flat>
                        <v-toolbar-title>
                          External Links on System
                        </v-toolbar-title>
                        <v-spacer></v-spacer>
                        <v-text-field
                          v-model="linkSearch"
                          append-icon="mdi-magnify"
                          label="Search"
                          single-line
                          hide-details
                          dense
                          clearable
                        ></v-text-field>
                      </v-toolbar>
                    </template>

                    <template v-slot:[`item.type`]="{ item }">
                      <v-chip small outlined color="primary">
                        <v-icon left small>
                          {{
                            item.type === "Video"
                              ? "mdi-play-circle-outline"
                              : "mdi-web"
                          }}
                        </v-icon>
                        {{ item.type }}
                      </v-chip>
                    </template>

                    <template v-slot:[`item.title`]="{ item }">
                      <span
                        :class="item.status === 1 ? '' : 'text--disabled'"
                        >{{ item.title }}</span
                      >
                    </template>

                    <template v-slot:[`item.description`]="{ item }">
                      {{ item.description || "-" }}
                    </template>

                    <template v-slot:[`item.platform`]="{ item }">
                      {{ item.platform || "-" }}
                    </template>

                    <template v-slot:[`item.url`]="{ item }">
                      <a
                        v-if="item.url"
                        :href="item.url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <v-icon small color="primary">mdi-open-in-new</v-icon>
                        Open
                      </a>
                      <span v-else>-</span>
                    </template>
                    <template v-slot:[`item.created_on`]="{ item }">
                      {{ item.created_on | dateReOrderRMtime }}
                    </template>

                    <template v-slot:[`item.actions`]="{ item }">
                      <v-btn
                        v-if="item.status === 1"
                        icon
                        color="error"
                        title="Remove resource"
                        @click.stop="askDeleteLink(item)"
                      >
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                      <v-chip v-else small color="grey" text-color="white">
                        Record removed
                      </v-chip>
                    </template>

                    <template v-slot:no-data>
                      <v-alert type="info" outlined class="ma-4">
                        No external links found. Add one above.
                      </v-alert>
                    </template>
                  </v-data-table>
                </v-container>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-alert-circle-outline</v-icon>
          Remove Resource
        </v-card-title>
        <v-card-text>
          <p>
            You are about to remove
            <strong>{{ pendingDelete ? pendingDelete.title : "" }}</strong
            >.
          </p>
          <p class="mb-0">
            The resource will no longer be available to learners. Do you wish to
            proceed?
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelDelete" :disabled="deleteLoading">
            Cancel
          </v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleteLoading">
            Confirm Removal
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { required, maxLength, integer } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  M_OU,
  LOAD_LEARNING_MATERIALS_EBOOKS,
  GET_LEARNING_MATERIALS_EBOOKS,
  IS_LEARNING_MATERIALS_EBOOKS_LOADED,
  SAVE_NEW_LEARNING_MATERIAL_EBOOK,
  DELETE_LEARNING_MATERIAL_EBOOK,
  LOAD_LEARNING_MATERIALS_EXTERNAL_LINKS,
  GET_LEARNING_MATERIALS_EXTERNAL_LINKS,
  IS_LEARNING_MATERIALS_EXTERNAL_LINKS_LOADED,
  SAVE_NEW_LEARNING_MATERIAL_EXTERNAL_LINK,
  DELETE_LEARNING_MATERIAL_EXTERNAL_LINK,
} from "@/store/mutation-list";

export default {
  name: "ManageLearningMaterials",

  validations: {
    ebookForm: {
      title: { required, maxLength: maxLength(150) },
      description: { maxLength: maxLength(500) },
      fileUrl: { required, maxLength: maxLength(500) },
      author: { maxLength: maxLength(100) },
      fileSize: { integer },
      coverImageUrl: { maxLength: maxLength(500) },
    },
    linkForm: {
      type: { required },
      title: { required, maxLength: maxLength(150) },
      description: { maxLength: maxLength(500) },
      url: { required, maxLength: maxLength(500) },
      platform: { maxLength: maxLength(100) },
      coverImageUrl: { maxLength: maxLength(500) },
    },
  },

  data() {
    return {
      activeTab: "tab-ebooks",

      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard/home" },
        {
          text: "Manage Learning Materials",
          disabled: true,
          href: "/dashboard/managelearningmaterials",
        },
      ],

      // eBooks
      ebookSearch: "",
      ebookSubmitting: false,
      ebookForm: {
        title: "",
        description: "",
        fileUrl: "",
        author: "",
        fileSize: "",
        coverImageUrl: "",
      },
      ebookHeaders: [
        { text: "ID", value: "resource_id", width: "70px" },
        { text: "Title", value: "title" },
        { text: "Description", value: "description" },
        { text: "Author", value: "author", width: "140px" },
        { text: "Size", value: "file_size", width: "110px" },
        { text: "File", value: "file_url", sortable: false, width: "90px" },
        { text: "Created On", value: "created_on", width: "160px" },
        { text: "Actions", value: "actions", sortable: false, width: "140px" },
      ],

      // External links
      linkSearch: "",
      linkSubmitting: false,
      linkTypes: ["Website", "Video"],
      linkForm: {
        type: "Website",
        title: "",
        description: "",
        url: "",
        platform: "",
        coverImageUrl: "",
      },
      linkHeaders: [
        { text: "ID", value: "resource_id", width: "70px" },
        { text: "Type", value: "type", width: "130px" },
        { text: "Title", value: "title" },
        { text: "Description", value: "description" },
        { text: "Platform", value: "platform", width: "140px" },
        { text: "Link", value: "url", sortable: false, width: "90px" },
        { text: "Created On", value: "created_on", width: "160px" },
        { text: "Actions", value: "actions", sortable: false, width: "140px" },
      ],

      // Deletion
      deleteDialog: false,
      deleteLoading: false,
      pendingDelete: null,
      pendingDeleteKind: null,
    };
  },

  computed: {
    ...mapGetters({
      getEbooks: `${M_OU}${GET_LEARNING_MATERIALS_EBOOKS}`,
      isEbookListLoaded: `${M_OU}${IS_LEARNING_MATERIALS_EBOOKS_LOADED}`,
      getExternalLinks: `${M_OU}${GET_LEARNING_MATERIALS_EXTERNAL_LINKS}`,
      isExternalLinkListLoaded: `${M_OU}${IS_LEARNING_MATERIALS_EXTERNAL_LINKS_LOADED}`,
    }),

    // ---- eBook validation messages ----
    ebookTitleErrors() {
      const errors = [];
      if (!this.$v.ebookForm.title.$dirty) return errors;
      if (!this.$v.ebookForm.title.required) errors.push("Title is required.");
      if (!this.$v.ebookForm.title.maxLength)
        errors.push("Title cannot exceed 150 characters.");
      return errors;
    },

    ebookDescriptionErrors() {
      const errors = [];
      if (!this.$v.ebookForm.description.$dirty) return errors;
      if (!this.$v.ebookForm.description.maxLength)
        errors.push("Description cannot exceed 500 characters.");
      return errors;
    },

    ebookFileUrlErrors() {
      const errors = [];
      if (!this.$v.ebookForm.fileUrl.$dirty) return errors;
      if (!this.$v.ebookForm.fileUrl.required)
        errors.push("File URL is required.");
      if (!this.$v.ebookForm.fileUrl.maxLength)
        errors.push("URL cannot exceed 500 characters.");
      return errors;
    },

    ebookAuthorErrors() {
      const errors = [];
      if (!this.$v.ebookForm.author.$dirty) return errors;
      if (!this.$v.ebookForm.author.maxLength)
        errors.push("Author cannot exceed 100 characters.");
      return errors;
    },

    ebookFileSizeErrors() {
      const errors = [];
      if (!this.$v.ebookForm.fileSize.$dirty) return errors;
      if (!this.$v.ebookForm.fileSize.integer)
        errors.push("File size must be a whole number.");
      return errors;
    },

    ebookCoverImageUrlErrors() {
      const errors = [];
      if (!this.$v.ebookForm.coverImageUrl.$dirty) return errors;
      if (!this.$v.ebookForm.coverImageUrl.maxLength)
        errors.push("Cover image URL cannot exceed 500 characters.");
      return errors;
    },

    // ---- External link validation messages ----
    linkTypeErrors() {
      const errors = [];
      if (!this.$v.linkForm.type.$dirty) return errors;
      if (!this.$v.linkForm.type.required)
        errors.push("Please select a resource type.");
      return errors;
    },

    linkTitleErrors() {
      const errors = [];
      if (!this.$v.linkForm.title.$dirty) return errors;
      if (!this.$v.linkForm.title.required) errors.push("Title is required.");
      if (!this.$v.linkForm.title.maxLength)
        errors.push("Title cannot exceed 150 characters.");
      return errors;
    },

    linkDescriptionErrors() {
      const errors = [];
      if (!this.$v.linkForm.description.$dirty) return errors;
      if (!this.$v.linkForm.description.maxLength)
        errors.push("Description cannot exceed 500 characters.");
      return errors;
    },

    linkUrlErrors() {
      const errors = [];
      if (!this.$v.linkForm.url.$dirty) return errors;
      if (!this.$v.linkForm.url.required) errors.push("URL is required.");
      if (!this.$v.linkForm.url.maxLength)
        errors.push("URL cannot exceed 500 characters.");
      return errors;
    },

    linkPlatformErrors() {
      const errors = [];
      if (!this.$v.linkForm.platform.$dirty) return errors;
      if (!this.$v.linkForm.platform.maxLength)
        errors.push("Platform cannot exceed 100 characters.");
      return errors;
    },

    linkCoverImageUrlErrors() {
      const errors = [];
      if (!this.$v.linkForm.coverImageUrl.$dirty) return errors;
      if (!this.$v.linkForm.coverImageUrl.maxLength)
        errors.push("Cover image URL cannot exceed 500 characters.");
      return errors;
    },
  },

  created() {
    this.loadEbooks();
    this.loadExternalLinks();
  },

  methods: {
    ...mapActions({
      loadEbooks: `${M_OU}${LOAD_LEARNING_MATERIALS_EBOOKS}`,
      addEbook: `${M_OU}${SAVE_NEW_LEARNING_MATERIAL_EBOOK}`,
      removeEbook: `${M_OU}${DELETE_LEARNING_MATERIAL_EBOOK}`,
      loadExternalLinks: `${M_OU}${LOAD_LEARNING_MATERIALS_EXTERNAL_LINKS}`,
      addExternalLink: `${M_OU}${SAVE_NEW_LEARNING_MATERIAL_EXTERNAL_LINK}`,
      removeExternalLink: `${M_OU}${DELETE_LEARNING_MATERIAL_EXTERNAL_LINK}`,
    }),

    showAlert(type, title, message) {
      this.$fire({ title, text: message, type, timer: 3000 });
    },

    formatFileSize(bytes) {
      const size = Number(bytes);
      if (!size) return "-";
      if (size < 1024) return `${size} B`;
      if (size < 1048576) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / 1048576).toFixed(1)} MB`;
    },

    // ---- eBooks ----
    resetEbookForm() {
      this.ebookForm = {
        title: "",
        description: "",
        fileUrl: "",
        author: "",
        fileSize: "",
        coverImageUrl: "",
      };
      this.$v.ebookForm.$reset();
    },

    submitEbook() {
      this.$v.ebookForm.$touch();
      if (this.$v.ebookForm.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix the errors before submitting."
        );
        return;
      }
      this.ebookSubmitting = true;
      this.addEbook({
        title: this.ebookForm.title,
        description: this.ebookForm.description,
        file_url: this.ebookForm.fileUrl,
        author: this.ebookForm.author,
        file_size: this.ebookForm.fileSize,
        cover_image_url: this.ebookForm.coverImageUrl,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert("success", "Success", "eBook added successfully.");
            this.resetEbookForm();
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to add eBook. Please try again."
            );
          }
        })
        .finally(() => {
          this.ebookSubmitting = false;
        });
    },

    askDeleteEbook(item) {
      this.pendingDelete = item;
      this.pendingDeleteKind = "ebook";
      this.deleteDialog = true;
    },

    // ---- External links ----
    resetLinkForm() {
      this.linkForm = {
        type: "Website",
        title: "",
        description: "",
        url: "",
        platform: "",
        coverImageUrl: "",
      };
      this.$v.linkForm.$reset();
    },

    submitLink() {
      this.$v.linkForm.$touch();
      if (this.$v.linkForm.$invalid) {
        this.showAlert(
          "error",
          "Validation Error",
          "Please fix the errors before submitting."
        );
        return;
      }
      this.linkSubmitting = true;
      this.addExternalLink({
        type: this.linkForm.type,
        title: this.linkForm.title,
        description: this.linkForm.description,
        url: this.linkForm.url,
        platform: this.linkForm.platform,
        cover_image_url: this.linkForm.coverImageUrl,
      })
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert(
              "success",
              "Success",
              "External link added successfully."
            );
            this.resetLinkForm();
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to add external link. Please try again."
            );
          }
        })
        .finally(() => {
          this.linkSubmitting = false;
        });
    },

    askDeleteLink(item) {
      this.pendingDelete = item;
      this.pendingDeleteKind = "link";
      this.deleteDialog = true;
    },

    // ---- Deletion ----
    cancelDelete() {
      this.deleteDialog = false;
      this.pendingDelete = null;
      this.pendingDeleteKind = null;
    },

    confirmDelete() {
      if (!this.pendingDelete) return;
      const resourceId = this.pendingDelete.resource_id;
      const request =
        this.pendingDeleteKind === "ebook"
          ? this.removeEbook(resourceId)
          : this.removeExternalLink(resourceId);

      this.deleteLoading = true;
      request
        .then((res) => {
          if (res === "SUCCESS") {
            this.showAlert("success", "Removed", "Resource has been removed.");
          } else {
            this.showAlert(
              "error",
              "Error",
              "Failed to remove the resource. Please try again."
            );
          }
        })
        .finally(() => {
          this.deleteLoading = false;
          this.cancelDelete();
        });
    },
  },
};
</script>

<style scoped>
.section-subtitle {
  font-weight: 500;
}
</style>
